import { Controller } from "@hotwired/stimulus"

// Non-modal cookie-consent banner driven by the Zaraz Consent API
// (https://developers.cloudflare.com/zaraz/consent-management/api/).
//
// Zaraz's own consent modal opens as a modal <dialog>: until the visitor
// answers, the page behind it is inert — taps are swallowed, touch scrolling
// stops and assistive tech sees only the dialog. Zones that run consent
// management switch that modal off (consent.hideModal, `rake zaraz:consent`)
// and this banner asks instead; "settings" still opens the Zaraz modal for
// per-purpose choices. See .claude/docs/cookie-consent.md.
//
// Rendered on every layout; it only ever appears where the zone runs consent
// management (drv.ge has none: zaraz.consent is undefined there) and its modal
// is off. On a zone that still auto-opens the modal, the dialog opens a few ms
// after zarazConsentAPIReady (modal is still false at that point), so the first
// decision waits SETTLE_MS and the banner keeps re-checking for a moment.
const SETTLE_MS = 400
const WATCH_MS = 2000
const WATCH_EVERY_MS = 100

export default class extends Controller {
  // Zaraz writes this cookie when the visitor makes a choice (the zone config's
  // consent.cookieName, default "cf_consent"). It is the only "not asked yet"
  // signal: getAll() reports every purpose as false before any choice too.
  static values = { cookie: { type: String, default: "cf_consent" } }

  connect() {
    this.settled = false
    this.refresh = this.refresh.bind(this)
    this.start = this.start.bind(this)
    document.addEventListener("zarazConsentChoicesUpdated", this.refresh)
    document.addEventListener("zarazConsentAPIReady", this.start)
    if (this.consent) this.start()
  }

  disconnect() {
    document.removeEventListener("zarazConsentChoicesUpdated", this.refresh)
    document.removeEventListener("zarazConsentAPIReady", this.start)
    this.stopWatching()
  }

  accept() {
    const consent = this.consent
    if (!consent) return

    consent.setAll(true)
    // Events held back while consent was pending (the page view) go out now.
    consent.sendQueuedEvents()
    this.element.hidden = true
  }

  reject() {
    const consent = this.consent
    if (!consent) return

    consent.setAll(false)
    this.element.hidden = true
  }

  settings() {
    const consent = this.consent
    if (!consent) return

    this.element.hidden = true
    consent.modal = true
  }

  start() {
    if (this.settleTimer) return

    this.settleTimer = setTimeout(() => {
      this.settled = true
      this.refresh()
      this.watch = setInterval(this.refresh, WATCH_EVERY_MS)
      this.watchEnd = setTimeout(() => this.stopWatching(), WATCH_MS)
    }, SETTLE_MS)
  }

  stopWatching() {
    clearTimeout(this.settleTimer)
    clearInterval(this.watch)
    clearTimeout(this.watchEnd)
  }

  refresh() {
    const consent = this.consent
    if (!consent || !this.settled) return

    this.element.hidden = this.decided || !this.hasPurposes(consent) || consent.modal === true
  }

  hasPurposes(consent) {
    return Object.keys(consent.purposes || {}).length > 0
  }

  get decided() {
    return document.cookie.split("; ").some((pair) => pair.startsWith(`${this.cookieValue}=`))
  }

  get consent() {
    if (typeof zaraz === "undefined" || !zaraz.consent || !zaraz.consent.APIReady) return null
    return zaraz.consent
  }
}
